/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import './style/dateField.scss';

import React, {ChangeEvent, CSSProperties, KeyboardEventHandler, PureComponent} from 'react';
import {ChangeFunction, ControlVariant} from '../../util/interfaces';
import {DateTimeParts, DateType} from './dateFieldController';
import classNames from 'classnames';
import {ControlContext} from '../../util/controlContext';
import {BACKSPACE_CODE} from '../../util/constants';
import DatePicker from './datePicker/datePicker';
import TimePicker from './timePicker/timePicker';
import DateInput from './input/dateInput';
import {DVRInputControllerProps} from '../v2/inputController_v2';
import {colorIsWhite, convertColor, editColor} from '../../util/colorUtil';
import {pad} from '../../util/controlUtil';
import AwesomeIcon from '../../icon/awesomeIcon';
import WithBackground from '../../popup/withBackground';
import ButtonController from '../../button/buttonController';
import IDate, {IDateValue} from '@dvrd/idate';

export interface DateNumParts {
    day: number;
    month: number;
    year: number;
    hour: number;
    minute: number;
}

interface Props extends DVRInputControllerProps {
    onChange: ChangeFunction;
    onChangeText: (value: any, evt?: ChangeEvent, callback?: Function) => void;
    onKeyDown: KeyboardEventHandler;
    dateType: DateType;
    dateParts: DateTimeParts;
    pickerClass: string;
    disableFuture: boolean;
    disablePast: boolean;
    disableAfter?: IDateValue;
    disableBefore?: IDateValue;
    value: string;
    mask: string;
}

enum PickerState {DATE, TIME}

interface State {
    pickerOpen: boolean;
    pickerState: PickerState;
    pickerValue: DateNumParts;
}

export enum PickerKey {DAY = 'day', MONTH = 'month', YEAR = 'year'}

export default class DateField extends PureComponent<Props, State> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    constructor(props: Props) {
        super(props);
        this.state = {
            pickerOpen: false,
            pickerState: this.getDefaultPickerState(),
            pickerValue: this.getInitPickerValue()
        };
    }

    onMouseEnterSelector = (forState: PickerState) => (evt: MouseEvent) => {
        const {pickerState} = this.state;
        if (forState !== pickerState) {
            const target = evt.target as HTMLElement;
            target.style.color = editColor(.2, this.getBaseColor(false));
        }
    };

    onMouseLeaveSelector = (forState: PickerState) => (evt: MouseEvent) => {
        const {pickerState} = this.state;
        if (forState !== pickerState) {
            const target = evt.target as HTMLElement;
            target.style.color = this.getSelectorStyle(forState).color as string;
        }
    };

    onClosePicker = () => {
        this.setState({pickerOpen: false, pickerState: this.getDefaultPickerState()});
    };

    onOpenPicker = () => {
        this.setState({pickerOpen: true, pickerValue: this.getInitPickerValue()});
    };

    onChangeInternalValue = (key: PickerKey, value: number, add: boolean = true) => () => {
        const {pickerValue} = this.state;
        let nextValue: DateNumParts;
        if (add) {
            const currentMoment = this.getCurrentMoment(pickerValue).add(value, key);
            nextValue = {
                day: Math.min(pickerValue.day, currentMoment.daysInMonth()),
                month: currentMoment.month(),
                year: currentMoment.year(),
                hour: currentMoment.hours(),
                minute: currentMoment.minutes()
            };
        } else {
            nextValue = Object.assign({}, this.state.pickerValue, {[key]: value});
            const nextMoment = IDate.now().month(nextValue.month).year(nextValue.year).day(1),
                maxDays = nextMoment.daysInMonth();
            if (nextValue.day > maxDays)
                nextValue.day = maxDays;
        }
        this.setState({pickerValue: nextValue}, () => {
            const {dateType} = this.props;
            if (key === PickerKey.DAY) {
                if (dateType === DateType.DATE)
                    this.onSubmit();
                else if (dateType === DateType.DATETIME)
                    this.onChangePickerState(PickerState.TIME)();
            }
        });
    };

    onChangeTime = (hour: number, minute: number) => {
        this.setState({pickerValue: Object.assign({}, this.state.pickerValue, {hour, minute})});
    };

    onSubmit = () => {
        const {dateType, onChange} = this.props, {pickerValue} = this.state,
            date = `${pad(pickerValue.day)}-${pad(pickerValue.month + 1)}-${pickerValue.year}`,
            time = `${pad(pickerValue.hour)}:${pad(pickerValue.minute)}`;

        let value: string;
        switch (dateType) {
            case DateType.DATE:
                value = date;
                break;
            case DateType.DATETIME:
                value = `${date} ${time}`;
                break;
            case DateType.TIME:
                value = time;
                break;
        }
        onChange(value);
        this.onClosePicker();
    };

    onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (evt.key === BACKSPACE_CODE)
            this.onBackSpace(evt);
        else if (!/\d|ArrowRight|ArrowLeft|Escape|Esc|Enter|Tab/.test(evt.key)) {
            // Prevent everything else then allowed keys
            evt.preventDefault();
        } else {
            this.props.onKeyDown(evt);
        }
    };

    onBackSpace = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        const target = evt.target as HTMLInputElement, caretPos = target.selectionStart, selEnd = target.selectionEnd,
            currentValue = target.value;
        if (caretPos !== null && selEnd !== null && selEnd > caretPos) this.onDeleteSelection(evt);
        else if (caretPos && caretPos > 0) {
            const charToDelete = currentValue.charAt(caretPos - 1);
            evt.preventDefault();
            if (/[-: ]/.test(charToDelete)) {
                const nextValue = currentValue.substr(0, caretPos - 2) +
                    currentValue.substr(caretPos, currentValue.length);
                this.props.onChangeText(nextValue, undefined, () => {
                    target.selectionStart = caretPos - 2;
                    target.selectionEnd = caretPos - 2;
                });
            } else {
                const nextValue = currentValue.substr(0, caretPos - 1) +
                    currentValue.substr(caretPos, currentValue.length);
                this.props.onChangeText(nextValue, undefined, () => {
                    target.selectionStart = caretPos - 1;
                    target.selectionEnd = caretPos - 1;
                });
            }
        }
    };

    onDeleteSelection = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        evt.preventDefault();
        const target: HTMLInputElement = evt.target as HTMLInputElement, start = target.selectionStart,
            end = target.selectionEnd, currentValue: string = target.value;
        if (start !== null && end !== null) {
            let nextValue = '';
            for (let i = 0; i < currentValue.length; i++) {
                if (i >= start && i <= end) nextValue += ' ';
                else nextValue += currentValue.charAt(i);
            }
            this.props.onChangeText(nextValue, undefined, () => {
                target.selectionStart = start;
                target.selectionEnd = start;
            });
        }
    };

    onClickToday = () => {
        const today = IDate.now();
        this.setState({
            pickerValue: {
                month: today.month(),
                day: today.day(),
                minute: today.minutes(),
                hour: today.hours(),
                year: today.year()
            }
        }, () => {
            if (this.props.dateType === DateType.DATE) {
                this.onSubmit();
            } else if (this.state.pickerState === PickerState.TIME) this.scrollToCurrent();
        });
    };

    onChangePickerState = (nextState: PickerState) => () => {
        if (nextState !== this.state.pickerState)
            this.setState({pickerState: nextState});
    };

    onChangeInput = (value: IDate) => {
        this.setState({
            pickerValue: Object.assign({}, this.state.pickerValue, {
                day: value.day(),
                hour: value.hours(),
                minute: value.minutes(),
                month: value.month(),
                year: value.year()
            })
        }, () => {
            this.onSubmit();
        });
    };

    getDefaultPickerState = () => {
        const {dateType} = this.props;
        if (dateType === DateType.TIME) return PickerState.TIME;
        return PickerState.DATE;
    };

    getInitPickerValue = (): DateNumParts => {
        const dateParts = this.props.dateParts;
        const now = IDate.now();
        if (!isNaN(Number(dateParts.month))) dateParts.month = (Number(dateParts.month) - 1).toString();
        const merged: DateNumParts = {
            day: now.day(), month: now.month(), year: now.year(), hour: now.hours(), minute: now.minutes()
        };
        for (const [key, value] of Object.entries(dateParts)) {
            if(!value || !value.length || ['dd', 'mm', 'yyyy', 'hh'].includes(value)) continue;
            merged[key as keyof DateNumParts] = Number(value);
        }
        return merged;
        // return mergeWith(dateParts, {
        //     day: now.day(),
        //     month: now.month(),
        //     year: now.year(),
        //     hour: now.hours(),
        //     minute: now.minutes()
        // }, (source1: string, source2: number) => {
        //     if (!source1 || source1.length === 0 || ['dd', 'mm', 'yyyy', 'hh'].includes(source1)) return source2;
        //     return Number(source1);
        // });
    };

    scrollToCurrent = () => {
        const currentMoment = this.getCurrentMoment();
        const hour = currentMoment.hours();
        const minute = currentMoment.minutes();
        this.scrollTime('hour', hour);
        this.scrollTime('minute', minute);
    };

    getSelectorStyle = (forState: PickerState): CSSProperties => {
        const {pickerState} = this.state, styles: CSSProperties = {color: convertColor('color-gray-4')};
        if (pickerState === forState) styles.color = this.getBaseColor(false);
        return styles;
    };

    getBaseColor = (allowWhite: boolean = true): string => {
        let color = convertColor(this.context.baseColor);
        if (!allowWhite && colorIsWhite(color)) color = convertColor(this.context.contrastColor);
        return color;
    };

    getCurrentMoment = (value?: DateNumParts): IDate => {
        const {pickerValue} = this.state;
        const toUse: DateNumParts = value !== undefined ? value : pickerValue;
        return IDate.now().set({
            year: toUse.year,
            month: toUse.month,
            day: toUse.day,
            hours: toUse.hour,
            minutes: toUse.minute
        });
    };

    getInputValue = (): IDate | null => {
        const {value, dateType} = this.props;
        if (value.length) {
            if (dateType === DateType.DATE)
                return new IDate(value, 'DD-MM-YYYY');
            if (dateType === DateType.DATETIME)
                return new IDate(value, 'DD-MM-YYYY HH:mm');
            return new IDate(value, 'HH:mm');
        }
        return null;
    };

    scrollTime = (type: 'hour' | 'minute', id: number) => {
        const element = document.getElementById(`${type}-${pad(id)}`);
        if (element) element.scrollIntoView({block: 'center'});
    };

    getTodayText = () => this.state.pickerState === PickerState.TIME ? 'Nu' : 'Vandaag';

    renderPicker = () => {
        const {pickerState, pickerValue} = this.state,
            {pickerClass, dateType, dateParts, disablePast, disableFuture, disableBefore, disableAfter} = this.props;
        return (
            <div className={classNames('dvr-datepicker-container', pickerClass)}>
                {pickerState === PickerState.DATE &&
                    <DatePicker onChange={this.onChangeInternalValue} getCurrentMoment={this.getCurrentMoment}
                                pickerValue={pickerValue} dateParts={dateParts} disableAfter={disableAfter}
                                disableBefore={disableBefore} disableFuture={disableFuture} disablePast={disablePast}/>}
                {pickerState === PickerState.TIME &&
                    <TimePicker pickerValue={pickerValue} dateType={dateType} onChange={this.onChangeTime}/>}
                {dateType === DateType.DATETIME && this.renderStateSelector()}
                <div className='dvr-datepicker-footer'>
                    <ButtonController onClick={this.onClickToday} label={this.getTodayText()}
                                      type={ControlVariant.SIMPLE}
                                      primary={false}/>
                    {dateType != DateType.DATE &&
                        <ButtonController onClick={this.onSubmit} label='Oké' type={ControlVariant.SIMPLE}
                                          primary={false}/>}
                </div>
            </div>
        );
    };

    renderStateSelector = () => {
        return (
            <div className='dvr-pickerstate-container'>
                <AwesomeIcon name='calendar-alt' title='Datum' onClick={this.onChangePickerState(PickerState.DATE)}
                             className='selector' style={this.getSelectorStyle(PickerState.DATE)}
                             onMouseEnter={this.onMouseEnterSelector(PickerState.DATE)}
                             onMouseLeave={this.onMouseLeaveSelector(PickerState.DATE)}/>
                <AwesomeIcon name='clock' title='Tijd' onClick={this.onChangePickerState(PickerState.TIME)}
                             className='selector' style={this.getSelectorStyle(PickerState.TIME)}
                             onMouseEnter={this.onMouseEnterSelector(PickerState.TIME)}
                             onMouseLeave={this.onMouseLeaveSelector(PickerState.TIME)}/>
            </div>
        );
    };

    componentDidUpdate = (prevProps: Props, prevState: State) => {
        const {pickerState, pickerOpen} = this.state, prevPickerState = prevState.pickerState;
        if (pickerState === PickerState.TIME &&
            (prevPickerState === PickerState.DATE || (!prevState.pickerOpen && pickerOpen)))
            this.scrollToCurrent();
    };

    render = () => {
        const {dateType, label, error, fullWidth, onEnter, disabled} = this.props, {pickerOpen} = this.state;
        return (
            <>
                <DateInput onChange={this.onChangeInput} value={this.getInputValue()} dateType={dateType}
                           onEnter={onEnter} onClickOrnament={this.onOpenPicker} label={label} error={error}
                           fullWidth={fullWidth} disabled={disabled}/>
                <WithBackground onClose={this.onClosePicker} active={pickerOpen}>{this.renderPicker()}</WithBackground>
            </>
        );
    };
}